import { inject, Injectable } from '@angular/core';
import { FileDownloaderService } from '@data-terminal/utils/services/file-downloader.service';
import { UserMachinesService } from '@data-terminal/data-access/user-machines';
import { Machine } from '@data-terminal/shared-models/machine';
import { ConsumptionEntity, ConsumptionsPerDevice } from '@data-terminal/shared-models/consumption.model';
import {
    BatchInformation,
    OperationBatchInformation,
} from '@data-terminal/feature-dialogs/dialogs/batch-information-dialog/single-machine-batch-information/single-machine-batch-information.component';
import { MimeType } from '@data-terminal/shared-models/mime-type.enum';

export interface BatchCsvData {
    deviceId: string;
    operationName: string;
    materialId: string;
    consumption: number;
    batchId: string;
    updatedAt: string;
}

@Injectable({
    providedIn: 'root',
})
export class BatchInformationService {
    readonly #fileDownloaderService = inject(FileDownloaderService);
    readonly #userMachinesService = inject(UserMachinesService);
    allMachines: Machine[] | undefined = this.#userMachinesService.getAllMachines();

    prepareBatchInformation(consumptions: ConsumptionEntity[]): OperationBatchInformation {
        return consumptions.reduce(
            (acc, consumption) => {
                const operationId = this.#getOperationIdFromOperationPrimaryKey(consumption.operationPrimaryKey);
                if (!acc[operationId]) {
                    acc[operationId] = [];
                }

                acc[operationId].push({
                    deviceId: consumption.deviceId,
                    operationName: consumption.operationName ?? '',
                    materialId: consumption.materialId,
                    consumption: consumption.consumption,
                    batchId: consumption.batchId ?? '',
                    timestamp: consumption.updatedAt ? new Date(consumption.updatedAt).getTime() : undefined,
                });

                return acc;
            },
            {} as { [key: string]: BatchInformation[] }
        );
    }

    findMachineNameById(machineId: string): string {
        return this.allMachines?.find((machine) => machine.machineId === machineId)?.machineName ?? '';
    }

    exportBatchInformationToCsv(batchInformation: ConsumptionsPerDevice, jobId: string): void {
        const csvData = this.#convertBatchInformationToCsvData(batchInformation);
        this.#fileDownloaderService.download(csvData, `Batch_Info_${jobId}`, MimeType.CSV);
    }

    #convertBatchInformationToCsvData(batchInformation: ConsumptionsPerDevice): string {
        const mappedData = this.#mapBatchInformationToCsvData(batchInformation);
        const header = Object.keys(mappedData[0]).join(',');
        const rows = mappedData.map((obj) =>
            Object.values(obj)
                .map((value) => `"${value}"`)
                .join(',')
        );
        return [header, ...rows].join('\n');
    }

    #mapBatchInformationToCsvData(batchInformation: ConsumptionsPerDevice): BatchCsvData[] {
        return Object.values(batchInformation)
            .reduce((acc: ConsumptionEntity[], entry: ConsumptionEntity[]) => {
                return acc.concat(...Object.values(entry));
            }, [])
            .map((singleBatchInformation: ConsumptionEntity) => {
                return {
                    deviceId: singleBatchInformation.deviceId,
                    operationName: singleBatchInformation.operationName,
                    materialId: singleBatchInformation.materialId,
                    consumption: singleBatchInformation.consumption,
                    batchId: singleBatchInformation.batchId,
                    updatedAt: singleBatchInformation.updatedAt
                        ? new Date(singleBatchInformation.updatedAt).toUTCString()
                        : '',
                } as BatchCsvData;
            });
    }

    #getOperationIdFromOperationPrimaryKey(operationPrimaryKey: string): string {
        if (operationPrimaryKey.includes('__')) {
            return operationPrimaryKey.split('__')[1];
        } else {
            return operationPrimaryKey;
        }
    }
}
